// Latest | Standard ElevenLabs voice (LAN-1654), with the voice pickers on the
// Speak page (its voice menu and Chat Settings modal) and the settings page. It mirrors
// Stream::ElevenlabsModelPolicy: shown only for an ElevenLabs voice whose
// model the team chooses, so hidden for OpenAI, Inworld and Cartesia voices
// and where Rails always uses Eleven v4 (guided course, Japanese, French, Lucy).
import $ from 'jquery';
import SpeakApiClient from './chat/speak_api_client';

// Whether the team's choice applies to this voice. Voices are option values
// like 'maria_female_elevenlabs'.
export function toggleShown({ voice, language, guided, v4Languages, v4Voices }) {
  const parts = String(voice || '').split('_');
  if (parts.length < 2 || parts[parts.length - 1] !== 'elevenlabs') return false;
  return !(guided || v4Languages.includes(String(language || '').toLowerCase()) || v4Voices.includes(parts[0].toLowerCase()));
}

function parseList(value) {
  try {
    return JSON.parse(value || '[]');
  } catch (error) {
    return [];
  }
}

export class VoiceVersionToggle {
  constructor(root) {
    this.root = root;
    this.version = root.dataset.version;
    this.v4Languages = parseList(root.dataset.v4Languages);
    this.v4Voices = parseList(root.dataset.v4Voices);
    this.voiceSelect = document.querySelector(root.dataset.voiceSelect);
    this.topicCategory = root.dataset.topicCategory ? document.querySelector(root.dataset.topicCategory) : null;
    this.input = root.querySelector('.js-voice-version-input');
    const authToken = root.dataset.saveNow === 'true' ? document.querySelector('[data-auth-token]')?.dataset.authToken : null;
    this.api = authToken ? new SpeakApiClient(authToken) : null;

    root.querySelectorAll('.voice-version-option').forEach((button) => {
      button.addEventListener('click', (event) => {
        // In the Speak page's voice menu, keep the dropdown open.
        if (root.closest('.dropdown-menu')) event.stopPropagation();
        this.choose(button.dataset.value);
      });
    });
    this.setUpHelp(root.querySelector('.js-voice-version-help'));
    // The voice select is a select2 widget on both pages, which fires jQuery
    // change events. The form's own changes (call mode version, which swaps the
    // voice list) arrive as native events, after the page's handlers run.
    const refresh = () => this.refresh();
    if (this.voiceSelect) $(this.voiceSelect).on('change', refresh);
    this.voiceSelect?.form?.addEventListener('change', () => setTimeout(refresh, 0));
    if (this.topicCategory) this.topicCategory.addEventListener('change', refresh);
    // Picking a topic card (guided course or not) on the Speak page.
    document.addEventListener('click', (event) => {
      if (this.root.isConnected && event.target.closest?.('.js-main-chat-option-btn')) setTimeout(refresh, 0);
    });
    this.refresh();
  }

  // The (?) shows its text on a tap or click, as well as on hover, and any
  // other tap closes it. Without Bootstrap's tooltip, its title still shows on hover.
  setUpHelp(help) {
    const $help = help && $(help);
    if (!$help || typeof $help.tooltip !== 'function') return;

    $help.tooltip({
      trigger: 'manual',
      placement: 'top',
      container: 'body',
      template: '<div class="tooltip voice-version-tooltip" role="tooltip"><div class="arrow"></div><div class="tooltip-inner"></div></div>'
    });
    help.addEventListener('click', (event) => {
      event.preventDefault();
      // In the Speak page's voice menu, keep the dropdown open.
      if (this.root.closest('.dropdown-menu')) event.stopPropagation();
      $help.tooltip('toggle');
    });
    help.addEventListener('mouseenter', () => $help.tooltip('show'));
    help.addEventListener('mouseleave', () => $help.tooltip('hide'));
    document.addEventListener('click', (event) => {
      if (event.target.closest?.('.js-voice-version-help') !== help) $help.tooltip('hide');
    });
    $(this.root.closest('.dropdown')).on('hide.bs.dropdown', () => $help.tooltip('hide'));
  }

  shown() {
    return toggleShown({
      voice: this.voiceSelect?.value,
      language: this.root.dataset.language,
      guided: this.topicCategory?.value === 'guided',
      v4Languages: this.v4Languages,
      v4Voices: this.v4Voices
    });
  }

  refresh() {
    this.root.classList.toggle('d-none', !this.shown());
    this.root.querySelectorAll('.voice-version-option').forEach((button) => {
      const active = button.dataset.value === this.version;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  async choose(value) {
    if (value === this.version || !this.shown()) return;

    const previous = this.version;
    this.setVersion(value);
    if (!this.api) return;

    try {
      await this.api.updateLanguaSettings({ team: { chat_settings: { voice_version: value } } });
    } catch (error) {
      this.setVersion(previous);
    }
  }

  // The Speak page has two toggles for the team's one choice: keep both showing it.
  setVersion(value) {
    document.querySelectorAll('.js-voice-version-toggle').forEach((root) => {
      const toggle = root.voiceVersionToggle || (root === this.root ? this : null);
      if (!toggle) return;
      toggle.version = value;
      toggle.root.dataset.version = value;
      if (toggle.input) toggle.input.value = value;
      toggle.refresh();
    });
  }
}

function mountAll() {
  document.querySelectorAll('.js-voice-version-toggle').forEach((root) => {
    if (root.voiceVersionToggle) return;
    root.voiceVersionToggle = new VoiceVersionToggle(root);
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener('turbolinks:load', mountAll);
}
